import { useState } from "react";
import { Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

const STATUS_STYLE = {
  paid: "bg-emerald-50 text-emerald-700",
  pending: "bg-blue-50 text-blue-700",
  failed: "bg-red-50 text-red-700",
  expired: "bg-slate-100 text-slate-600",
  refunded: "bg-amber-50 text-amber-700",
};

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

export const BillingHistoryButton = ({ companyId, companyName, testId }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  const onOpenChange = async (next) => {
    setOpen(next);
    if (next) {
      setData(null);
      try {
        const res = await api.get(`/admin/companies/${companyId}/billing`);
        setData(res.data);
      } catch (err) {
        toast.error(errMsg(err));
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid={`${testId}-open`}
          className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
        >
          <Receipt className="mr-1.5 h-3.5 w-3.5" /> Billing
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold tracking-tight">
            Billing history · {companyName}
          </DialogTitle>
        </DialogHeader>

        {!data ? (
          <div className="py-10 text-center text-sm text-slate-500">Loading…</div>
        ) : (
          <div data-testid={`${testId}-panel`}>
            <div className="grid grid-cols-3 gap-4 rounded-lg border border-slate-200 bg-slate-50 p-5">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Current plan
                </div>
                <div
                  className="mt-1.5 font-display text-lg font-bold text-slate-900"
                  data-testid={`${testId}-plan`}
                >
                  {data.plan_name || "None"}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Status
                </div>
                <div className="mt-1.5">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      STATUS_STYLE[data.payment_status] || "bg-slate-100 text-slate-600"
                    }`}
                    data-testid={`${testId}-status`}
                  >
                    {data.payment_status}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Total paid
                </div>
                <div
                  className="mt-1.5 font-display text-lg font-bold text-slate-900"
                  data-testid={`${testId}-total`}
                >
                  ${data.total_paid.toFixed(2)}
                </div>
              </div>
            </div>

            <div className="mt-6">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Past charges
              </div>
              {data.charges.length === 0 ? (
                <p
                  className="mt-4 rounded-md border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500"
                  data-testid={`${testId}-empty`}
                >
                  No charges yet. Pick a plan to start this Vendor/Supplier's subscription.
                </p>
              ) : (
                <div className="mt-3 max-h-72 space-y-2 overflow-y-auto pr-1">
                  {data.charges.map((c) => (
                    <div
                      key={c.session_id}
                      data-testid={`${testId}-charge`}
                      className="flex items-center justify-between gap-4 rounded-md border border-slate-200 bg-white px-4 py-3"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900">{c.plan_name}</div>
                        <div className="mt-0.5 text-xs text-slate-500">{fmtDate(c.created_at)}</div>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="font-mono text-sm text-slate-800">
                          ${c.amount.toFixed(2)} {c.currency}
                        </span>
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            STATUS_STYLE[c.status] || "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {c.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
